import { Checkbox, CheckboxSize } from "@components/Checkbox";
import { ErrorMessage } from "@components/ErrorMessage";
import { Loading, LoadingIndicator } from "@components/LoadingIndicator";
import { Turnstile } from "@components/Turnstile";
import { AddTipAndDonationSummary } from "@components/donate/DonateV3/PaymentProcess/components/AddTipAndDonationSummary";
import { Chariot } from "@components/donate/DonateV3/PaymentProcess/components/Chariot";
import {
  AddressInput,
  DonorInfo,
} from "@components/donate/DonateV3/PaymentProcess/components/DonorInfo";
import { GoBackButton } from "@components/donate/DonateV3/PaymentProcess/components/GoBackButton";
import { OurPaypalButton } from "@components/donate/DonateV3/PaymentProcess/components/OurPaypalButton";
import {
  PageContainer,
  ProcessContainer,
} from "@components/donate/DonateV3/PaymentProcess/components/PageContainer";
import { PaymentMethodPicker } from "@components/donate/DonateV3/PaymentProcess/components/PaymentMethodPicker";
import {
  CONFIRMATION_POSTFIX,
  PaymentProcessRouteName,
  paymentProcessRouteNameToPathMap,
} from "@components/donate/DonateV3/PaymentProcess/components/PaymentProcessLink";
import { PaymentRequestButton } from "@components/donate/DonateV3/PaymentProcess/components/PaymentRequestButton";
import { SelectDonationFlowPaymentOption } from "@components/donate/DonateV3/PaymentProcess/components/SelectFlowPaymentOption";
import { ShareInfo } from "@components/donate/DonateV3/PaymentProcess/components/ShareInfo";
import { archiveDonation } from "@components/donate/DonateV3/PaymentProcess/helpers";
import { getMaxValueForPaymentOption } from "@components/donate/DonateV3/PaymentProcess/pages/Donate";
import { DonateButton } from "@components/donate/DonateV3/PaymentProcess/pages/Donate/DonateButton";
import { DonateFormContext } from "@components/donate/DonateV3/PaymentProcess/useDonateFormContext";
import {
  StepperType,
  useStepper,
} from "@components/donate/DonateV3/PaymentProcess/useStepper";
import { useSyncPaymentMethod } from "@components/donate/DonateV3/PaymentProcess/useSyncPaymentMethod";
import {
  validateAmountAndFrequency,
  validateCommentText,
  validateCreditsOnlyAmount,
} from "@components/donate/DonateV3/PaymentProcess/validators";
import {
  CreateOrUpdateDonationResult,
  DonateFormType,
  DonateModalAction,
  DONATE_FORM_ERROR,
  DONATE_STRIPE_ERROR,
} from "@components/donate/DonateV3/types";
import { COLLECT_ADDRESS, useCollectAddress } from "@components/donate/hooks";
import { css } from "@emotion/react";
import Big from "big.js";
import dynamic from "next/dynamic";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";

import { FundraiserResponse } from "@every.org/common/src/codecs/entities";
import { spacing } from "@every.org/common/src/display/spacing";
import {
  PaymentSourceType,
  DonationFlowPaymentOption,
  DonationFrequency,
} from "@every.org/common/src/entity/types";
import { disableDaimoSpec } from "@every.org/common/src/entity/types/configVariable";
import {
  getRoutePath,
  ClientRouteName,
  URLFormat,
  DONATE_HASH,
} from "@every.org/common/src/helpers/clientRoutes";
import { DAIMO_CRYPTO_CURRENCIES } from "@every.org/common/src/helpers/cryptoCurrency";

import { AuthContext } from "src/context/AuthContext";
import { useIsSuperAdmin } from "src/context/AuthContext/hooks";
import { AuthStatus } from "src/context/AuthContext/types";
import {
  useConfigVariable,
  UseConfigVariableStatus,
} from "src/context/ConfigVariableContext";
import { ContextNonprofit } from "src/context/NonprofitsContext/types";
import { useEdoRouter } from "src/hooks/useEdoRouter";
import { horizontalStackCss } from "src/theme/spacing";
import { logger } from "src/utility/logger";
import { getWindow } from "src/utility/window";

const DaimoPayButton = dynamic(() => import("./CryptoV2/DaimoPayButton"), {
  loading: () => <LoadingIndicator />,
});

export const DonationConfirmation = ({
  nonprofit,
  form,
  formContext,
  submitDonation,
  handleConfirmedDonation,
  createOrUpdateDonationResult,
  paymentOption,
  fundraiser,
  isManualDaf,
}: {
  nonprofit: ContextNonprofit;
  form: UseFormReturn<DonateFormType>;
  formContext: DonateFormContext;
  submitDonation: (
    formValues: DonateFormType
  ) => Promise<CreateOrUpdateDonationResult | undefined>;
  handleConfirmedDonation: (result: CreateOrUpdateDonationResult) => boolean;
  paymentOption: DonationFlowPaymentOption;
  fundraiser?: FundraiserResponse | null;
  createOrUpdateDonationResult?: CreateOrUpdateDonationResult;
  isManualDaf?: boolean;
}) => {
  const {
    formState: { errors },
    control,
    watch,
  } = form;
  const authState = useContext(AuthContext);

  const routerLocation = useLocation();
  const navigate = useNavigate();
  const router = useEdoRouter();
  const navigationType = useNavigationType();

  // The donor's address (DonorInfo/AddressInput's "location" field, not to
  // be confused with react-router's routerLocation above) - see
  // AddCreditCardProps.location/.isAddressRequired for how this reconciles
  // with the Payment Element's own address/ZIP collection.
  const donorLocation = watch("location");
  const { collectAddress, hasAddress } = useCollectAddress(formContext);
  const isAddressRequired =
    collectAddress === COLLECT_ADDRESS.REQUIRED && !hasAddress;

  useEffect(() => {
    const locationArray = routerLocation.pathname.split("/");
    if (
      navigationType === "POP" &&
      locationArray[3] &&
      locationArray[3] === "confirm"
    ) {
      navigate(`${DONATE_HASH}/${locationArray[2]}`);
    }
  }, [navigate, navigationType, routerLocation]);

  useEffect(() => {
    getWindow()?.scrollTo({ top: 0 });
  }, []);

  const clearFormError = useCallback(async () => {
    if (errors.DONATE_FORM_ERROR || errors.DONATE_STRIPE_ERROR) {
      form.clearErrors(DONATE_FORM_ERROR);
      form.clearErrors(DONATE_STRIPE_ERROR);
      await formContext.refreshNonce();
    }
  }, [form, formContext, errors]);

  const [showChariotButton, setShowChariotButton] = useState(
    paymentOption === DonationFlowPaymentOption.DAF &&
      !!createOrUpdateDonationResult
  );

  const {
    nonprofitMatchCampaign,
    minValue,
    maxValue,
    shorten,
    availableGivingCredit,
  } = formContext;
  const displayShareMatchForm =
    nonprofitMatchCampaign?.matchShare &&
    nonprofitMatchCampaign?.shareMatchAmount &&
    nonprofitMatchCampaign?.shareMatchAmount > 0;

  const amount = form.watch("amount");
  const frequency = form.watch("frequency");
  const paymentMethod = form.watch("paymentMethod");
  const selectedPaymentSource = form.watch("paymentSource");
  const firstName = form.watch("firstName");
  const lastName = form.watch("lastName");
  const email = form.watch("email");
  const privateNote = form.watch("privateNote");
  const commentText = form.watch("commentText");
  const currency = form.watch("currency");
  const cryptoCurrency = form.watch("cryptoCurrency");

  // Guest, one-time donation with a brand-new, not-saved card: there's no
  // saved PaymentSource to select for this flow, so the Donate button must
  // be enabled/visible even though no paymentSource has been selected -
  // submitDonation tokenizes the entered card and creates/confirms its
  // PaymentIntent itself, right before calling /donate.
  const isNewCardOneTimeGuest =
    paymentOption === DonationFlowPaymentOption.CREDIT_CARD &&
    authState.status === AuthStatus.LOGGED_OUT &&
    frequency === DonationFrequency.ONCE;

  const isRecurring = frequency !== DonationFrequency.ONCE;

  const canUseCreditsOnly =
    formContext.creditAmount.gt(0) && formContext.amountToCharge.eq(0);

  const [addBackupPaymentMethod, setAddBackupPaymentMethod] = useState(false);

  const creditsOnly = !addBackupPaymentMethod && canUseCreditsOnly;

  const showBackupPaymentMethodCheckbox = isRecurring && canUseCreditsOnly;

  // A donor (guest or logged-in) can always add a brand-new card instead of
  // picking a saved one - even with other saved sources already available
  // (e.g. a logged-in donor clicking "Add card") - so the Donate button
  // must stay visible (and actually submittable, see canSubmitNewCard
  // below) while the add-card element itself is on screen, not just once a
  // source has actually been selected. Fed by SelectPaymentSource's
  // onAddingCard callback.
  const [isAddingCard, setIsAddingCard] = useState(false);

  // Whether it's OK to submit without a selected paymentSource because a
  // brand-new card is being entered instead - submitDonation (see
  // PaymentProcess/index.tsx's isNewCard) tokenizes it and creates/confirms
  // its PaymentIntent right before calling /donate. isNewCardOneTimeGuest
  // covers the guest one-time case at first mount, before
  // SelectPaymentSource's onAddingCard effect has had a chance to fire;
  // isAddingCard covers every other case (logged-in donor, recurring,
  // adding a new card alongside existing saved ones, etc.). Mirrors the
  // same condition the Donate button's visibility is gated on below.
  // When credits fully cover the charge (backup-payment-method flow), or
  // when updating/retrying an existing donation, the add-card widget runs
  // in SetupIntent mode with its own "Add card" button instead (see
  // SelectPaymentSource) - there's no card for the Donate button itself to
  // submit until it's been added and auto-selected, and submitDonation's
  // UPDATE branch has no createNewCardPaymentIntentRef fallback to tokenize
  // one on submit.
  const canSubmitNewCard =
    formContext.donateAction !== DonateModalAction.UPDATE &&
    formContext.amountToCharge.gt(0) &&
    (isNewCardOneTimeGuest || isAddingCard);

  useSyncPaymentMethod({
    paymentOption,
    form,
    formContext,
    creditsOnly,
  });

  const isSuperAdmin = useIsSuperAdmin();
  const daimoDisabledConfig = useConfigVariable(disableDaimoSpec);
  const daimoDisabled =
    daimoDisabledConfig.status === UseConfigVariableStatus.SUCCESS &&
    daimoDisabledConfig.value &&
    !isSuperAdmin;

  const shouldUseDaimo =
    !daimoDisabled &&
    paymentOption === DonationFlowPaymentOption.CRYPTO &&
    cryptoCurrency &&
    DAIMO_CRYPTO_CURRENCIES.includes(cryptoCurrency);
  const [showDaimoButton, setShowDaimoButton] = useState(
    shouldUseDaimo && !!createOrUpdateDonationResult
  );

  const handleDonationComplete = useCallback(
    (result?: CreateOrUpdateDonationResult) => {
      const completedDonation = result || createOrUpdateDonationResult;
      if (paymentOption === DonationFlowPaymentOption.IRA) {
        navigate(
          paymentProcessRouteNameToPathMap[
            PaymentProcessRouteName.IRA_INSTRUCTIONS
          ]
        );
        return;
      }
      if (isManualDaf) {
        navigate(
          paymentProcessRouteNameToPathMap[
            PaymentProcessRouteName.DAF_MANUAL_INSTRUCTIONS
          ]
        );
        return;
      }
      if (paymentOption === DonationFlowPaymentOption.STOCKS) {
        navigate(
          paymentProcessRouteNameToPathMap[
            PaymentProcessRouteName.STOCKS_INSTRUCTIONS
          ]
        );
        return;
      }
      if (
        paymentOption === DonationFlowPaymentOption.CRYPTO &&
        // If the payment goes through Daimo, then we don't need the
        // crypto-complete page
        !showDaimoButton
      ) {
        navigate(
          paymentProcessRouteNameToPathMap[
            PaymentProcessRouteName.CRYPTO_COMPLETE
          ]
        );
        return;
      }
      if (completedDonation && !handleConfirmedDonation(completedDonation)) {
        if (formContext.donateAction === DonateModalAction.UPDATE) {
          router.push(
            getRoutePath({
              name: ClientRouteName.MY_GIVING_RECURRING,
              format: URLFormat.RELATIVE,
              query: {},
            })
          );
          return;
        }
        if (!formContext.isGiftCardPurchase) {
          navigate(
            displayShareMatchForm
              ? paymentProcessRouteNameToPathMap[
                  PaymentProcessRouteName.SHARE_MATCH
                ]
              : paymentProcessRouteNameToPathMap[
                  PaymentProcessRouteName.THANK_YOU
                ]
          );
        }
      }
    },
    [
      formContext.donateAction,
      createOrUpdateDonationResult,
      handleConfirmedDonation,
      navigate,
      router,
      displayShareMatchForm,
      paymentOption,
      formContext.isGiftCardPurchase,
      isManualDaf,
      showDaimoButton,
    ]
  );

  /**
   * On first render, if amount and frequency are not valid, go back to the
   * previous step
   */
  useEffect(() => {
    if (
      paymentOption !== DonationFlowPaymentOption.CRYPTO &&
      paymentOption !== DonationFlowPaymentOption.STOCKS
    ) {
      if (
        !validateAmountAndFrequency({
          frequency,
          amount: amount?.toString() || "",
          minValue,
          maxValue: getMaxValueForPaymentOption(
            paymentOption,
            currency,
            maxValue.amount
          ),
          shorten,
          setError: form.setError,
        }) ||
        !validateCreditsOnlyAmount({
          amount: amount?.toString() || "",
          availableGivingCredit,
          paymentMethod,
          shorten,
          setError: form.setError,
        }) ||
        !validateCommentText(
          privateNote,
          form.setError,
          "privateNote",
          nonprofit.metadata?.privateNoteLimit
        ) ||
        !validateCommentText(commentText, form.setError, "commentText")
      ) {
        navigate(routerLocation.pathname.replace(CONFIRMATION_POSTFIX, ""), {
          replace: true,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Manual DAF, STOCK, and CRYPTO case: Reset the donation state when the user returns to the confirmation page from the instruction page, but do not archive/expire the donation.
   * Paypal: initialize a donation as a blank slate and fill in the details when the paypal donation completes
   */
  const resetCreatedDonation = useCallback(
    (errorMessage?: string) => {
      const donationId = createOrUpdateDonationResult?.donation.id;
      setShowChariotButton(false);
      if (donationId) {
        if (
          [
            DonationFlowPaymentOption.DAF,
            DonationFlowPaymentOption.IRA,
            DonationFlowPaymentOption.STOCKS,
            DonationFlowPaymentOption.CRYPTO,
          ].includes(paymentOption)
        ) {
          formContext.refreshNonce();
          formContext.setCreateOrUpdateDonationResult(undefined);
        } else {
          archiveDonation(donationId, formContext);
        }
      }
      errorMessage &&
        form.setError(DONATE_FORM_ERROR, {
          type: "string",
          message: errorMessage,
        });
    },
    [
      createOrUpdateDonationResult?.donation.id,
      form,
      formContext,
      paymentOption,
    ]
  );

  useEffect(() => {
    if (
      formContext.donateAction !== DonateModalAction.UPDATE &&
      (paymentOption === DonationFlowPaymentOption.DAF ||
        paymentOption === DonationFlowPaymentOption.IRA ||
        paymentOption === DonationFlowPaymentOption.STOCKS ||
        paymentOption === DonationFlowPaymentOption.CRYPTO) &&
      createOrUpdateDonationResult
    ) {
      resetCreatedDonation();
      // forbid the user from going back to the instructions page without clicking the "Get instructions" button
      navigate(routerLocation.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * If the current donation details don't match the details of the created
   * donation, then reset the donation state so we can initialize a new dafpay
   * button.
   */
  useEffect(() => {
    const { amount, frequency, amountToEveryOrg } = form.getValues();
    if (
      showChariotButton &&
      createOrUpdateDonationResult &&
      (!createOrUpdateDonationResult.donation.value.amount.eq(amount || 0) ||
        createOrUpdateDonationResult.donation.frequency !== frequency ||
        !createOrUpdateDonationResult.donation.tipAmount.amount.eq(
          amountToEveryOrg || 0
        ))
    ) {
      resetCreatedDonation();
    }
  }, [
    createOrUpdateDonationResult,
    form,
    resetCreatedDonation,
    showChariotButton,
  ]);

  const handleSubmit = async (formValues: DonateFormType) => {
    if (
      [
        DonationFlowPaymentOption.CREDIT_CARD,
        DonationFlowPaymentOption.BANK,
      ].includes(paymentOption) &&
      !formValues.paymentSource &&
      !creditsOnly &&
      !canSubmitNewCard
    ) {
      return;
    }
    logger.info({
      message: "Submitted!",
      data: {
        ...formValues,
        paymentTab: paymentOption,
      },
    });
    const result = await submitDonation({
      ...formValues,
      paymentTab: paymentOption,
    });

    if (result) {
      if (shouldUseDaimo) {
        setShowDaimoButton(true);
      } else if (
        paymentOption !== DonationFlowPaymentOption.PAYPAL &&
        paymentOption !== DonationFlowPaymentOption.VENMO &&
        (paymentOption !== DonationFlowPaymentOption.DAF || isManualDaf)
      ) {
        handleDonationComplete(result);
      } else if (
        paymentOption === DonationFlowPaymentOption.DAF &&
        !isManualDaf
      ) {
        setShowChariotButton(true);
      }
    }

    return result;
  };

  const submit = form.handleSubmit(handleSubmit, (errors) => {
    // Only log actual form validation errors, not payment processing errors
    // Payment errors are stored in DONATE_STRIPE_ERROR and logged separately
    if (errors.DONATE_STRIPE_ERROR) {
      logger.info({
        message: "Payment processing error when confirming donation",
        data: errors,
      });
    } else {
      logger.error({
        message: "Error validating form when confirming donation",
        data: errors,
      });
    }
  });

  const isCrypto = paymentOption === DonationFlowPaymentOption.CRYPTO;
  const cashAmount = form.watch("amount") || 0;
  const cryptoAmount = form.watch("cryptoPledgeAmount") || 0;
  const amountBig = useMemo(
    () => new Big(isCrypto ? cryptoAmount : cashAmount),
    [isCrypto, cashAmount, cryptoAmount]
  );

  const showStepper =
    isManualDaf || paymentOption === DonationFlowPaymentOption.STOCKS;
  const stepperType = isManualDaf ? StepperType.MANUAL_DAF : StepperType.STOCKS;
  const stepperActiveStep = isManualDaf ? 1 : 0;
  const stepper = useStepper(stepperType, stepperActiveStep);

  if (authState.status === AuthStatus.LOADING) {
    return <Loading />;
  }

  return (
    <PageContainer>
      {formContext.skipAmountAndFrequency ? (
        <SelectDonationFlowPaymentOption
          selectedPaymentOption={paymentOption}
          paymentRequestReadyStatus={
            formContext.paymentRequestInitializer.readyStatus
          }
          paymentRequestIsApplePay={
            formContext.paymentRequestInitializer.isApplePay
          }
          paymentFlowOptions={formContext.paymentFlowOptions}
          showMorePaymentOptions={formContext.showMorePaymentOptions}
          setShowMorePaymentOptions={formContext.setShowMorePaymentOptions}
        />
      ) : (
        <GoBackButton
          form={form}
          formContext={formContext}
          paymentOption={paymentOption}
        />
      )}
      <ProcessContainer>
        <form
          css={css`
            > :not(:last-child) {
              margin-bottom: ${spacing.l};
            }
          `}
          onSubmit={submit}
        >
          {showStepper && stepper}
          <Controller
            control={control}
            name={"amountToEveryOrg"}
            render={({ field: { onChange, value } }) => (
              <AddTipAndDonationSummary
                onChange={onChange}
                value={value}
                amountBig={amountBig}
                form={form}
                formContext={formContext}
                nonprofit={nonprofit}
                fundraiser={fundraiser}
                paymentOption={paymentOption}
              />
            )}
          />
          {authState.status === AuthStatus.LOGGED_OUT ? (
            <DonorInfo form={form} formContext={formContext} />
          ) : (
            <AddressInput form={form} formContext={formContext} />
          )}
          {!formContext.hideShareInfo && (
            <ShareInfo
              form={form}
              formContext={formContext}
              nonprofit={nonprofit}
              fundraiser={fundraiser}
            />
          )}
          {showBackupPaymentMethodCheckbox && (
            <div css={horizontalStackCss.xs}>
              <Checkbox
                size={CheckboxSize.LARGE}
                checked={addBackupPaymentMethod}
                data-tname="addBackupPaymentMethodCheckbox"
                name="addBackupPaymentMethodCheckbox"
                onClick={() => setAddBackupPaymentMethod((prev) => !prev)}
              />
              <label htmlFor="addBackupPaymentMethodCheckbox">
                Add a backup payment method to keep your monthly donation going
                if your credits run out
              </label>
            </div>
          )}
          {/* Add a backup payment method to keep your monthly donation going if your credits run out */}
          {[
            DonationFlowPaymentOption.BANK,
            DonationFlowPaymentOption.CREDIT_CARD,
          ].includes(paymentOption) && (
            <React.Fragment>
              <Controller
                control={control}
                name="paymentSource"
                render={({ field: { onChange } }) => (
                  <PaymentMethodPicker
                    paymentSourceType={
                      paymentOption === DonationFlowPaymentOption.BANK
                        ? PaymentSourceType.ACH_DEBIT
                        : PaymentSourceType.CARD
                    }
                    amountToCharge={formContext.amountToCharge}
                    amountBig={formContext.amountBig}
                    tipAmount={formContext.totalAmountBig.minus(
                      formContext.amountBig
                    )}
                    creditAmount={formContext.creditAmount}
                    creditsOnly={creditsOnly}
                    donateAction={formContext.donateAction}
                    renewablePaymentSourceRequired={
                      formContext.renewablePaymentSourceRequired
                    }
                    isInternational={formContext.isInternational}
                    isGiftCardPurchase={formContext.isGiftCardPurchase}
                    paymentSources={formContext.paymentSources}
                    setPaymentSources={formContext.setPaymentSources}
                    selectedPaymentSource={selectedPaymentSource}
                    onChange={onChange}
                    firstName={firstName}
                    lastName={lastName}
                    email={email}
                    clearFormError={clearFormError}
                    setFormError={form.setError}
                    nonprofit={nonprofit}
                    frequency={frequency}
                    createNewCardPaymentIntentRef={
                      formContext.createNewCardPaymentIntentRef
                    }
                    location={donorLocation}
                    isAddressRequired={isAddressRequired}
                    onAddingCard={setIsAddingCard}
                  />
                )}
              />
              {/* TODO: figure out how to fix this error message */}
              {/* {errors.paymentSource && <p>{errors.paymentSource}</p>} */}
            </React.Fragment>
          )}
          {(errors.DONATE_FORM_ERROR?.message ||
            errors.DONATE_STRIPE_ERROR?.message) && (
            <ErrorMessage
              text={
                errors.DONATE_FORM_ERROR?.message ||
                errors.DONATE_STRIPE_ERROR?.message ||
                ""
              }
            />
          )}
          {formContext.isDoubleDonation && (
            <ErrorMessage text={formContext.doubleDonationMessage} />
          )}
          <Turnstile
            onSuccess={(token) => formContext.setCfTurnstileToken(token)}
            onExpire={() => formContext.setCfTurnstileToken(undefined)}
            resetSignal={formContext.cfTurnstileResetCount}
          />
          <div
            data-bloom-block
            css={css`
              display: flex;
              justify-content: center;
            `}
          >
            {createOrUpdateDonationResult && showDaimoButton ? (
              <DaimoPayButton
                createOrUpdateDonationResult={createOrUpdateDonationResult}
                onComplete={() =>
                  handleDonationComplete(createOrUpdateDonationResult)
                }
                dollarValue={
                  formContext.totalCryptoAmount &&
                  formContext.cryptoTokenRate &&
                  formContext.cryptoTokenRate.rate > 0
                    ? new Big(formContext.totalCryptoAmount)
                        .times(formContext.cryptoTokenRate.rate)
                        .toFixed(2)
                        .toString()
                    : undefined
                }
              />
            ) : showChariotButton &&
              createOrUpdateDonationResult &&
              paymentOption === DonationFlowPaymentOption.DAF &&
              !isManualDaf ? (
              <Chariot
                form={form}
                formContext={formContext}
                createOrUpdateDonationResult={createOrUpdateDonationResult}
                handleConfirmedDonation={handleConfirmedDonation}
              />
            ) : paymentOption === DonationFlowPaymentOption.PAYPAL ||
              paymentOption === DonationFlowPaymentOption.VENMO ? (
              <OurPaypalButton
                amount={formContext.amountToCharge.toString()}
                frequency={frequency}
                handleSubmit={() =>
                  handleSubmit({
                    ...form.getValues(),
                    paymentTab: DonationFlowPaymentOption.PAYPAL,
                  })
                }
                finishCallback={handleDonationComplete}
                cancelCallback={resetCreatedDonation}
                enableVenmo={paymentOption === DonationFlowPaymentOption.VENMO}
              />
            ) : !creditsOnly &&
              paymentOption === DonationFlowPaymentOption.PAYMENT_REQUEST ? (
              <PaymentRequestButton
                donateAction={formContext.donateAction}
                form={form}
                formContext={formContext}
                nonprofit={nonprofit}
                submitDonation={submitDonation}
                handleConfirmedDonation={handleConfirmedDonation}
              />
            ) : (
              (selectedPaymentSource !== undefined ||
                creditsOnly ||
                canSubmitNewCard ||
                [
                  DonationFlowPaymentOption.PAYPAL,
                  DonationFlowPaymentOption.VENMO,
                  DonationFlowPaymentOption.CRYPTO,
                  DonationFlowPaymentOption.DAF,
                  DonationFlowPaymentOption.IRA,
                  DonationFlowPaymentOption.STOCKS,
                ].includes(paymentOption)) && (
                <DonateButton
                  totalAmountBig={
                    paymentOption === DonationFlowPaymentOption.CRYPTO &&
                    formContext.totalCryptoAmount
                      ? formContext.totalCryptoAmount
                      : formContext.totalAmountBig
                  }
                  currency={
                    paymentOption === DonationFlowPaymentOption.CRYPTO &&
                    cryptoCurrency
                      ? cryptoCurrency
                      : formContext.currency
                  }
                  donateAction={formContext.donateAction}
                  paymentOption={paymentOption}
                  isManualDaf={isManualDaf}
                  submitting={form.formState.isSubmitting}
                  disabled={
                    form.formState.isSubmitting ||
                    !!form.formState.errors.amount?.message
                  }
                  creditsOnly={creditsOnly}
                  data-tname={`clickDonate__${paymentOption}`}
                  isDoubleDonation={formContext.isDoubleDonation}
                  isDaimo={shouldUseDaimo}
                />
              )
            )}
          </div>
          {paymentOption === DonationFlowPaymentOption.BANK && (
            <p
              css={css`
                font-size: 0.875rem;
                line-height: 1.25rem;
                color: var(--textSecondaryColor);
                margin-top: 8px;
              `}
            >
              {`By clicking "Donate" you authorize Every.org to debit your bank account for the amount indicated ${
                frequency === DonationFrequency.ONCE
                  ? "on a one-time basis"
                  : `on a recurring ${
                      frequency === DonationFrequency.YEARLY
                        ? "annual"
                        : frequency.toLowerCase()
                    } basis starting ${new Date().toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })} until you cancel`
              }.`}
            </p>
          )}
        </form>
      </ProcessContainer>
    </PageContainer>
  );
};
